import { PROBLEM_POINTS } from "@/lib/brand";
import { MapPinned, PenLine, FileCheck } from "lucide-react";

const MARQUEE = "Verbal feedback disappears at 60km/h.";

const CAMERA_POINTS = [
  { icon: MapPinned, lead: "Mapped and timestamped", text: "every lesson logged with its GPS route and events as they happen" },
  { icon: PenLine, lead: "Signed and verified", text: "your e-signature on every lesson record" },
  { icon: FileCheck, lead: "One page, ready to hand over", text: "a clear lesson report for any parent, insurer or regulator, and a dashboard that tracks every learner" },
];

export default function Problem() {
  return (
    <section id="problem" data-testid="problem-section" className="relative bg-white">
      {/* editorial marquee */}
      <div className="border-y border-[#E5E5E5] py-6 overflow-hidden">
        <div className="marquee-track">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight text-[#2B2B2B]/10 mx-8">
              {MARQUEE}
            </span>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14 lg:py-16">
        {/* Story 1 — verbal feedback */}
        <div data-testid="problem-slide-0" className="grid lg:grid-cols-12 gap-x-12 gap-y-6">
          <div className="lg:col-span-5">
            <p className="overline text-[#2BBFB3]">The problem</p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02] text-[#2B2B2B]">
              Verbal feedback disappears at 60&nbsp;km/h.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg text-[#2B2B2B]/70 leading-relaxed">
              Learners can't process complex instruction while driving. End-of-lesson debriefs blur into
              vague impressions. Parents are left guessing what to practise.{" "}
              <span className="text-[#2B2B2B] font-medium">DriveAssure fixes this with structured, spatial, cognitively compressed feedback.</span>
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {PROBLEM_POINTS.map((p, i) => (
                <div key={i} className="rounded-2xl border border-[#E5E5E5] bg-[#F7F7F7] p-4">
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#2BBFB3]/12 text-[#1FA097]"><p.icon size={18} /></span>
                  <p className="mt-3 text-sm text-[#2B2B2B] font-medium leading-snug">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="my-14 lg:my-16 border-t border-[#E5E5E5]" aria-hidden="true" />

        {/* Story 2 — cameras are coming */}
        <div data-testid="problem-slide-1" className="grid lg:grid-cols-12 gap-x-12 gap-y-6">
          <div className="lg:col-span-5">
            <p className="overline text-[#F5A623]">2027 reforms</p>
            <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02] text-[#2B2B2B]">
              Cameras Are Coming. Footage may not be Enough.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-lg text-[#2B2B2B]/70 leading-relaxed">
              From 2027, every South Australian driver training vehicle needs a camera and GPS. Footage
              shows what happened, not what you taught, why, or the learner's reaction. Raw footage has no
              context.{" "}
              <span className="text-[#2B2B2B] font-medium">DriveAssure gives you the record that tells the story fast.</span>
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {CAMERA_POINTS.map((p, i) => (
                <div key={i} className="rounded-2xl border border-[#E5E5E5] bg-[#F7F7F7] p-4">
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#F5A623]/15 text-[#B57608]"><p.icon size={18} /></span>
                  <p className="mt-3 text-sm text-[#2B2B2B] leading-snug">
                    <span className="font-semibold">{p.lead}:</span> {p.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
